import React, { useState } from 'react';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  Divider,
  Grid,
  Link,
  Typography,
} from 'web-library';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { TFunction, useTranslation } from 'react-i18next';

import { makeStyles } from 'tss-react/mui';
import { StatusCodes } from 'http-status-codes';
import { candidateService } from '../../../api';
import ApplicationHeaderDetails from './summary/ApplicationHeaderDetails';
import PersonalDetails from './particulars/PersonalDetails';
import ContactDetails from './particulars/ContactDetails';
import ExamSelectedSubjects from './ExamReviewSubjects';
import ExamFeeDetails from '../../../components/ExamFeeDetails';
import ExamTransactions from './summary/ExamTransactions';
import RouteKey from '../../../routes/key';
import { usePostCreateExamAmendment } from '../../../api/candidate/exam/@candidate/queries-services/queries';
import NextOfKinContactDetails from './particulars/NextOfKinContactDetails';
import DownloadProgressModal from '../../../components/modals/DownloadProgressModal';
import { FileUploadState } from '../../../models/File';

interface ExamSummaryPathParams {
  uuid?: string;
}

const useStyles = makeStyles()((theme) =>
({
  container: {
    alignItems: 'center',
  },
  errorText: {
    color: theme.color.state.stateError,
  },
  dialogBold: {
    fontWeight: 'bold',
  },
  paymentButton: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  tableCaption: {
    paddingTop: theme.spacing(6),
    display: 'inline-flex'
  },
  examFeeDetails: {
    paddingBottom: theme.spacing(6),
  },
  selectedSubjects: {
    paddingTop: theme.spacing(6),
  },
  amendmentError: {
    marginBottom: theme.spacing(3),
  },
  buttonContainer: {
    marginBottom: theme.spacing(7),
  },
  withdrawalLink: {
    display: 'inline-flex'
  }
}));

const { amendment, exam, withdrawal, error } = RouteKey.child;
const {
  examRegistrations,
  coordinatorExamRegistrations,
  coordinatorViewSummary,
} = RouteKey.exam.child;
const { generic } = RouteKey.error.child;
const { FORBIDDEN } = StatusCodes;
const { registration } = RouteKey.amendment.child;
const { examRegistrations: withdrawalRoute, instruction } = RouteKey.withdrawal.child;

const getBackLabel = (groupExamRegUuid: string | null, t: TFunction<'translation'>): string =>
  groupExamRegUuid
    ? t('exam->>RegistrationSummary.BackToRegistrationSummary')
    : t('exam->>RegistrationSummary.BackToRegisteredExams');
const getBackUrl = (groupExamRegUuid: string | null) =>
  groupExamRegUuid
    ? `/${exam}/${coordinatorExamRegistrations.route}/${groupExamRegUuid}/${coordinatorViewSummary.route}`
    : `/${exam}/${examRegistrations.route}`;

const ExamSummary = ({ isPaymentLink = false }: { isPaymentLink?: boolean }) => {
  const location = useLocation<any>();
  const { uuid } = useParams<ExamSummaryPathParams>();
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();
  const [amendmentError, setAmendmentError] = useState<string>();
  const [fileStatus, setFileStatus] = useState(FileUploadState.IDLE);
  const groupExamRegUuid = new URLSearchParams(location.search).get('groupExamRegUuid');
  const { refetch, isFetching } = candidateService.exam.candidate.useGetCandidateDownloadSummary(
    (download) => {
      const newWindow = window.open(download?.url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
    },
    (_) => {
      setFileStatus(FileUploadState.FAILED);
    },
    uuid
  );
  const { mutate, status } = usePostCreateExamAmendment(
    () => history.push(`/${amendment}/${examRegistrations.route}/${registration.route}`),
    ({ response }) => {
      if (response.data?.code === '1604') {
        setAmendmentError(t('exam->>RegistrationSummary.RegistrationIsProcessing'));
      } else {
        setAmendmentError(response?.data.message);
      }
    }
  );
  const { data } = candidateService.exam.candidate.useGetExamRegistrationSummary(uuid, (errors) => {
    if (errors.response?.status === FORBIDDEN) {
      history.push(`/${error}/${generic}`);
    }
  });

  const isIPsle =
    data?.groupedLevelFees?.length &&
    data?.groupedLevelFees.filter((item) => item.examLevel === 'iPSLE').length;

  const handleCloseDownloadErrorPrompt = () => {
    setFileStatus(FileUploadState.IDLE);
  };

  return (
    <>
      {data && (
        <>
          <ContentTemplate isContinuable type="full">
            <ApplicationHeaderDetails
              onDownloadAction={() => refetch()}
              isDownloading={isFetching}
              applicationDetails={data.applicationStatus}
              displayRegistrationType
              candidateName={groupExamRegUuid ? data.candidateDetails.name : ''}
              isDisplayEntryProof={!groupExamRegUuid}
              entryProofDownloadModels={data.entryProofDownloadModels}
            />
            <PersonalDetails examCandidateDetail={data.candidateDetails} defaultExpanded />
            <ContactDetails contactDetails={data.candidateDetails} />
            <NextOfKinContactDetails {...data.candidateDetails} />
            <ExamSelectedSubjects className={classes.selectedSubjects} {...data} />
            <ExamFeeDetails
              className={classes.examFeeDetails}
              {...data}
              gstRegistrationNumber={
                !isIPsle || data.payment.gstIndicator ? data.gstRegistrationNumber : ''
              }
            />
            <Divider />
            <ExamTransactions
              {...data}
              groupExamRegUuid={groupExamRegUuid || ''}
              candidateName={data.candidateDetails.name || ''}
            />
          </ContentTemplate>
          {!isPaymentLink && (
            <ContentTemplate continuous>
              <Box className={classes.buttonContainer}>
                {amendmentError && (
                  <Typography variant="body2" color="error" className={classes.amendmentError}>
                    {amendmentError}
                  </Typography>
                )}
                <Grid container direction="row" alignItems="flex-start" spacing={3}>
                  <Grid xs={12} md={5}>
                    <Button
                      id="button-selectSubject-back"
                      fullWidth
                      variant="outlined"
                      onClick={() => history.push(getBackUrl(groupExamRegUuid))}
                    >
                      {getBackLabel(groupExamRegUuid, t)}
                    </Button>
                  </Grid>
                  <Grid xs={12} md={4}>
                    {uuid && data.amendmentWindowOpened && !groupExamRegUuid && (
                      <ApiButton
                        id="button-amend-subjects"
                        fullWidth
                        type="button"
                        status={status}
                        onClick={() => mutate({ uuid })}
                      >
                        {t('exam->>RegistrationSummary.AmendSubjects')}
                      </ApiButton>
                    )}
                  </Grid>
                </Grid>
                {data.withdrawalWindowOpened && !groupExamRegUuid && (
                  <Box className={classes.tableCaption}>
                    <Typography variant="caption" className={classes.dialogBold}>
                      {t('exam->>RegistrationSummary.WithdrawInstruction')}&nbsp;
                      <Link
                          className={classes.withdrawalLink}
                          id="withdrawal-link"
                          onClick={() => {
                            history.push(
                                `/${withdrawal}/${withdrawalRoute.route}/${instruction.route}`
                            );
                          }}
                      >
                        <Typography variant="caption" className={classes.dialogBold}>
                          {t('exam->>ResultDetail.click here.')}
                        </Typography>
                      </Link>
                    </Typography>
                  </Box>
                )}
              </Box>
            </ContentTemplate>
          )}
          {fileStatus === FileUploadState.FAILED && (
            <DownloadProgressModal
              modalState
              fileDownloadStatus={FileUploadState.FAILED}
              onDialogClose={handleCloseDownloadErrorPrompt}
            />
          )}
        </>
      )}
    </>
  );
};

export default ExamSummary;
